// The screens around signing in (sign-in, sign-up, password reset, invitations, the workspace picker): the brand
// panel on the left, one centred column on the right, and the fields those screens share.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Icon, Input, type InputProps } from "../ui";

export const authTitle = { margin: 0, font: "800 38px/1.1 var(--font-display)", letterSpacing: "-0.02em", color: "var(--text-strong)" } as const;
export const authText = { margin: 0, font: "400 16px/1.5 var(--font-body)", color: "var(--text-body)" } as const;
export const authNote = { margin: 0, textAlign: "center", font: "var(--type-body-sm)", fontSize: 15, color: "var(--text-muted)" } as const;

/** The brand panel on the left and one centred column on the right; the footer sits at the bottom of the column's side. */
export function AuthFrame({ children, footer, center = false, width = 400 }: { children?: ReactNode; footer?: ReactNode; center?: boolean; width?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(320px,.8fr) minmax(0,1.2fr)", minHeight: "100vh", background: "#fff" }} className="avp-invite">
      <aside style={{ position: "relative", overflow: "hidden", background: "var(--gradient-glow),var(--gradient-deep)", color: "#fff", padding: "44px 52px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 24 }}>
        <img src="/logo-wordmark-inverse.png" alt="AutoVouch" style={{ height: 42, alignSelf: "flex-start" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <h2 style={{ font: "800 clamp(38px,3.8vw,52px)/1.04 var(--font-display)", letterSpacing: "-0.02em", margin: 0, color: "#fff" }}>Vouched<br />on arrival.</h2>
          <p style={{ margin: 0, maxWidth: 380, font: "400 17px/1.5 var(--font-body)", color: "var(--av-sky)" }}>Every supplier invoice checked against its PO in Syncore the minute it lands. Only the exceptions reach you.</p>
        </div>
        <span style={{ font: "13px var(--font-body)", color: "var(--av-sky)" }}>Syncore is a trademark of Facilisgroup, LLC.</span>
      </aside>
      <main style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px clamp(24px,5vw,64px)", position: "relative" }}>
        {children && (
          <div style={{ width: "100%", maxWidth: width, display: "flex", flexDirection: "column", gap: 28, alignItems: center ? "center" : "stretch", textAlign: center ? "center" : "left", animation: "av-rise var(--dur-slow) var(--ease-out)" }}>
            {children}
          </div>
        )}
        {footer && <span style={{ position: "absolute", bottom: 32, font: "14px var(--font-body)", color: "var(--text-muted)", textAlign: "center" }}>{footer}</span>}
      </main>
    </div>
  );
}

export function AuthHeading({ eyebrow, title, text }: { eyebrow?: ReactNode; title: ReactNode; text?: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: eyebrow ? 14 : 10 }}>
      {eyebrow && <span style={{ font: "600 15px var(--font-body)", color: "var(--text-muted)" }}>{eyebrow}</span>}
      <h1 style={authTitle}>{title}</h1>
      {text && <p style={authText}>{text}</p>}
    </div>
  );
}

export function AuthBadge({ tone, icon }: { tone: "warn" | "bad" | "info"; icon: string }) {
  const [bg, fg] = { warn: ["var(--status-review-bg)", "var(--status-review-fg)"], bad: ["var(--status-mismatch-bg)", "var(--status-mismatch-fg)"], info: ["var(--av-blue-100)", "var(--text-link)"] }[tone];
  return <span aria-hidden="true" style={{ width: 56, height: 56, borderRadius: "50%", background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={icon} size={26} /></span>;
}

export function AuthActions({ children, column = false }: { children: ReactNode; column?: boolean }) {
  return <div style={{ display: "flex", flexDirection: column ? "column" : "row", gap: column ? 18 : 10, alignItems: column ? "stretch" : "center", flexWrap: "wrap" }}>{children}</div>;
}

/** A link-looking button, for steps inside one screen (Change, Resend, Forgot password?). */
export function TextButton({ children, onClick, disabled = false, style }: { children: ReactNode; onClick: () => void; disabled?: boolean; style?: CSSProperties }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ border: 0, background: "none", padding: 0, font: "700 15px var(--font-body)", color: disabled ? "var(--text-muted)" : "var(--text-link)", cursor: disabled ? "default" : "pointer", ...style }}>
      {children}
    </button>
  );
}

/** Who this step is for, with a way back to change it. */
export function EmailChip({ email, onChange }: { email: string; onChange?: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}>
      <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--gradient-fresh)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", font: "800 13px var(--font-display)", flex: "none" }}>{email.slice(0, 1).toUpperCase()}</span>
      <span style={{ flex: 1, minWidth: 0, font: "600 16px var(--font-body)", color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</span>
      {onChange && <TextButton onClick={onChange} style={{ fontSize: 14 }}>Change</TextButton>}
    </div>
  );
}

/** An email the invite fixes: shown in a locked field. */
export function LockedEmail({ email, hint }: { email: string; hint?: ReactNode }) {
  return <Input size="lg" label="Email" value={email} readOnly disabled hint={hint} trailingAddon={<Icon name="lock" size={17} />} />;
}

/** A password field with a show/hide toggle. */
export function PasswordInput({ label = "Password", ...rest }: Omit<InputProps, "type" | "trailingAddon">) {
  const [shown, setShown] = useState(false);
  return (
    <Input
      {...rest}
      size="lg"
      label={label}
      type={shown ? "text" : "password"}
      trailingAddon={
        <button type="button" aria-label={shown ? "Hide password" : "Show password"} onClick={() => setShown(!shown)} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, margin: "0 -8px 0 0", border: 0, borderRadius: 8, background: "transparent", color: "var(--text-muted)", cursor: "pointer" }}>
          <Icon name={shown ? "eye-off" : "eye"} size={19} />
        </button>
      }
    />
  );
}

/** Four bars that fill as a new password gets longer and more mixed. */
export function StrengthBar({ score }: { score: number }) {
  const words = ["", "Too short", "Needs more variety", "Good", "Strong. Nice one."];
  const colour = score >= 3 ? "var(--status-matched)" : score === 2 ? "var(--status-review)" : "var(--status-mismatch)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div aria-hidden="true" style={{ display: "flex", gap: 6 }}>
        {[1, 2, 3, 4].map((n) => <span key={n} style={{ flex: 1, height: 6, borderRadius: 99, background: n <= score ? colour : "var(--border-subtle)", transition: "background var(--dur-fast) var(--ease-out)" }} />)}
      </div>
      {score > 0 && <span style={{ font: "700 13px var(--font-body)", color: score >= 3 ? "var(--status-matched-fg)" : "var(--text-muted)" }}>{words[score]}</span>}
    </div>
  );
}

/** One box per digit: typing moves on, Backspace moves back, and a pasted code fills every box. */
export function CodeInput({ length, value, onChange, invalid = false, autoFocus = true, label = "Code" }: { length: number; value: string; onChange: (value: string) => void; invalid?: boolean; autoFocus?: boolean; label?: string }) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    // Focus only when the code is first shown, not as each digit changes.
    if (autoFocus) boxes.current[Math.min(value.length, length - 1)]?.focus();
  }, [autoFocus, length]);

  const set = (digits: string, focusAt: number) => {
    onChange(digits.slice(0, length));
    boxes.current[Math.max(0, Math.min(focusAt, length - 1))]?.focus();
  };
  const size = length > 6 ? 46 : 56;
  return (
    <fieldset style={{ margin: 0, padding: 0, border: 0, minWidth: 0 }}>
      <legend style={{ padding: 0, marginBottom: 8, font: "var(--type-label)", color: "var(--text-strong)" }}>{label}</legend>
      <div style={{ display: "flex", gap: length > 6 ? 8 : 10 }}>
        {Array.from({ length }, (_, i) => {
          const filled = i < value.length;
          return (
            <input
              key={i}
              ref={(el) => { boxes.current[i] = el; }}
              aria-label={`Digit ${i + 1}`}
              aria-invalid={invalid || undefined}
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              maxLength={length}
              value={value[i] ?? ""}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                if (!digits) return;
                // A pasted or autofilled code arrives in one box.
                if (digits.length > 1) set((value.slice(0, i) + digits), i + digits.length);
                else set(value.slice(0, i) + digits + value.slice(i + 1), i + 1);
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  e.preventDefault();
                  if (value[i]) set(value.slice(0, i) + value.slice(i + 1), i);
                  else if (i > 0) set(value.slice(0, i - 1) + value.slice(i), i - 1);
                }
                if (e.key === "ArrowLeft" && i > 0) boxes.current[i - 1]?.focus();
                if (e.key === "ArrowRight" && i < length - 1) boxes.current[i + 1]?.focus();
              }}
              onFocus={(e) => e.currentTarget.select()}
              style={{
                width: size, height: size + 8, boxSizing: "border-box", padding: 0, textAlign: "center",
                font: "800 26px var(--font-display)", color: "var(--text-strong)",
                border: `1.5px solid ${invalid ? "var(--status-mismatch)" : filled ? "var(--border-strong)" : "var(--border-default)"}`,
                borderRadius: "var(--radius-md)", background: filled || invalid ? "var(--surface-card)" : "var(--surface-sunken)",
                outline: "none",
              }}
            />
          );
        })}
      </div>
    </fieldset>
  );
}

/** A line under a field or a step: red for a problem. */
export function FormError({ children }: { children: ReactNode }) {
  return (
    <span role="alert" style={{ display: "flex", alignItems: "flex-start", gap: 8, font: "var(--type-body-sm)", fontSize: 14, lineHeight: 1.4, color: "var(--status-mismatch-fg)" }}>
      <Icon name="circle-alert" size={16} style={{ flex: "none", marginTop: 1 }} />
      <span>{children}</span>
    </span>
  );
}

/** Seconds left before a code can be sent again. */
export function useCountdown(seconds: number) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (left <= 0) return;
    const timer = window.setTimeout(() => setLeft(left - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [left]);
  return { left, restart: (next = seconds) => setLeft(next) };
}
